import { FC, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { DownOutlined } from '@ant-design/icons';

import { SetRegistrationView } from '~features/shared/locale';
import { RoutesUrls } from '~shared/lib/router';
import { useTranslation } from '~shared/lib/i18n';

import {
  AuthSider,
  Button,
  Dropdown,
  Logo,
  MenuIcon,
  MenuProps,
  SiteHeader as SiteHeaderComponent,
  SiteLink,
  useWindowInnerWidth,
} from '~shared/ui';
import { useCollapsed, useSetCollapsed } from '~features/shared/collapse';
import { useResolvedMobileAuthTheme } from '~shared/lib/mobile-auth';

export interface SiteHeaderProps extends Partial<ComponentWithChild> {}

export const SiteHeader: FC<SiteHeaderProps> = () => {
  const { t } = useTranslation();
  const windoWidth = useWindowInnerWidth();
  const collapsed = useCollapsed();
  const setCollapsed = useSetCollapsed();

  const resolvedTheme = useResolvedMobileAuthTheme();

  const { pathname } = useLocation();

  const activeParentKey = pathname.startsWith(RoutesUrls.applicationStatus)
    ? 'applicationStatus'
    : pathname.startsWith(RoutesUrls.familyRelation)
    ? 'familyRelation'
    : pathname.startsWith(RoutesUrls.parentRegistration)
    ? 'parentRegistration'
    : pathname.startsWith(RoutesUrls.siteFaq)
    ? 'siteFaq'
    : pathname.startsWith(RoutesUrls.parent)
    ? 'parent'
    : '';

  const activeDooKey = pathname.startsWith(RoutesUrls.statistics)
    ? 'statistics'
    : pathname.startsWith(RoutesUrls.main)
    ? 'main'
    : '';

  const isParentGroupActive = activeParentKey !== '';
  const isDooGroupActive = activeDooKey !== '';

  const [parentExpanded, setParentExpanded] = useState(isParentGroupActive);
  const [dooExpanded, setDooExpanded] = useState(isDooGroupActive);

  useEffect(() => {
    if (isParentGroupActive) {
      setParentExpanded(true);
    }
  }, [isParentGroupActive]);

  useEffect(() => {
    if (isDooGroupActive) {
      setDooExpanded(true);
    }
  }, [isDooGroupActive]);

  const handleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleParentToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setParentExpanded((v) => !v);
  };

  const handleDooToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDooExpanded((v) => !v);
  };

  const parentMenuItems: MenuProps['items'] = [
    {
      key: 'parent',
      label: (
        <NavLink
          to={RoutesUrls.parent}
          className={({ isActive }) => (isActive ? 'text-primary' : '')}
        >
          {t('site:main') as string}
        </NavLink>
      ),
    },
    {
      key: 'parentRegistration',
      label: (
        <NavLink
          to={RoutesUrls.parentRegistration}
          className={({ isActive }) => (isActive ? 'text-primary' : '')}
        >
          {t('site:parentRegistration') as string}
        </NavLink>
      ),
    },

    {
      key: 'applicationStatus',
      label: (
        <NavLink
          to={RoutesUrls.applicationStatus}
          className={({ isActive }) => (isActive ? 'text-primary' : '')}
        >
          {t('site:applicationStatus.title') as string}
        </NavLink>
      ),
    },
    {
      key: 'familyRelation',
      label: (
        <NavLink
          to={RoutesUrls.familyRelation}
          className={({ isActive }) => (isActive ? 'text-primary' : '')}
        >
          {t('site:checkFamilyRelation') as string}
        </NavLink>
      ),
    },
    {
      key: 'siteFaq',
      label: (
        <NavLink
          to={RoutesUrls.siteFaq}
          className={({ isActive }) => (isActive ? 'text-primary' : '')}
        >
          {t('faq:title') as string}
        </NavLink>
      ),
    },
  ];

  const buttons = () => {
    if (windoWidth > 768) {
      return (
        <div className="flex gap-8 items-center sm:hidden">
          <Dropdown
            menu={{
              items: parentMenuItems,
              selectedKeys: activeParentKey ? [activeParentKey] : [],
            }}
            trigger={['hover']}
          >
            <span
              className={`flex items-center gap-1 text-[15px] whitespace-nowrap cursor-pointer ${
                isParentGroupActive ? 'text-primary' : ''
              }`}
            >
              {t('site:parent') as string}
              <DownOutlined className="text-[11px]" />
            </span>
          </Dropdown>
          <SiteLink path={RoutesUrls.kindergartens} title={t('site:kindergartens') as string} />
          <SiteLink path={RoutesUrls.main} title={t('site:doo') as string} />

          <SiteLink path={RoutesUrls.openData} title={t('pages.openData') as string} />
          {/* {!window.location.origin.includes('balalyk') ? ( */}
          <SiteLink path={RoutesUrls.voucher} title="Ваучер" />
          {/* ) : null} */}
          <SiteLink path={RoutesUrls.monDocumentsSitePage} title={t('pages.documents')} />
          <div className="flex gap-5">
            <Button className="text-primary border-solid border-primary" size="middle">
              <NavLink to={RoutesUrls.login}>{t('auth:buttons.signIn')}</NavLink>
            </Button>
            <SetRegistrationView />
          </div>
        </div>
      );
    }

    return (
      <>
        <div onClick={handleCollapse} className="cursor-pointer flex items-center hidden sm:flex">
          <MenuIcon isDark={resolvedTheme === 'dark'} />
        </div>
      </>
    );
  };

  return (
    <>
      <SiteHeaderComponent>
        <div className="flex justify-between w-full items-center">
          <Logo />
        </div>
        {buttons()}
      </SiteHeaderComponent>

      <AuthSider>
        <div className="flex flex-col gap-5 w-full items-start text-left">
          <div className="w-full">
            <div
              onClick={handleParentToggle}
              className={`flex items-center justify-between cursor-pointer text-[15px] ${
                isParentGroupActive ? 'text-primary' : ''
              }`}
            >
              <span>{t('site:parent') as string}</span>
              <DownOutlined
                className={`text-[11px] transition-transform ${parentExpanded ? 'rotate-180' : ''}`}
              />
            </div>
            {parentExpanded && (
              <div className="flex flex-col gap-4 mt-4 pl-3 items-start text-left">
                <SiteLink path={RoutesUrls.parent} title={t('site:main') as string} />
                <SiteLink
                  path={RoutesUrls.parentRegistration}
                  title={t('site:parentRegistration') as string}
                />
                <SiteLink
                  path={RoutesUrls.applicationStatus}
                  title={t('site:applicationStatus.title') as string}
                />
                <SiteLink
                  path={RoutesUrls.familyRelation}
                  title={t('site:checkFamilyRelation') as string}
                />
                <SiteLink path={RoutesUrls.siteFaq} title={t('faq:title') as string} />
              </div>
            )}
          </div>
          <SiteLink path={RoutesUrls.kindergartens} title={t('site:kindergartens') as string} />
          <div className="w-full">
            <div
              onClick={handleDooToggle}
              className={`flex items-center justify-between cursor-pointer text-[15px] ${
                isDooGroupActive ? 'text-primary' : ''
              }`}
            >
              <span>{t('site:doo') as string}</span>
              <DownOutlined
                className={`text-[11px] transition-transform ${dooExpanded ? 'rotate-180' : ''}`}
              />
            </div>
            {dooExpanded && (
              <div className="flex flex-col gap-4 mt-4 pl-3 items-start text-left">
                <SiteLink path={RoutesUrls.main} title={t('site:main') as string} />
                <SiteLink
                  path={RoutesUrls.statistics}
                  title={t('site:statisticsTitle') as string}
                />
              </div>
            )}
          </div>
          <SiteLink path={RoutesUrls.openData} title={t('pages.openData') as string} />
          <SiteLink path={RoutesUrls.voucher} title="Ваучер" />
          <SiteLink path={RoutesUrls.monDocumentsSitePage} title={t('pages.documents')} />
          <div className="grid gap-[32px] mt-[50px] w-full">
            <Button className="text-primary border-solid border-primary" size="middle">
              <NavLink to={RoutesUrls.login}>{t('auth:buttons.signIn')}</NavLink>
            </Button>
            <div className="grid grid-cols-2 gap-5 w-full">
              <SetRegistrationView />
            </div>
          </div>
        </div>
      </AuthSider>
    </>
  );
};
